// src/utils/consent.js
//
// Analytics and marketing scripts load only after the visitor says yes
// (DPDP Act 2023: consent must be free, specific and as easy to withdraw as
// to give). Until then MintLink runs with essential storage only.
//
// What waits for consent:
//   - Google Analytics 4 (ga.js events are no-ops while gtag is absent)
//   - Microsoft Clarity (session replay and heatmaps)
//   - Trustpilot invitation JS (sends a buyer's email to Trustpilot)
//   - a seller's own Meta Pixel (metaPixel.js checks hasAnalyticsConsent)
//
// These used to sit in public/index.html and so ran on every route from the
// first byte, checkout and creator storefronts included.
//
// Clarity additionally NEVER records buyer-side pages (checkout, payment,
// downloads, tickets, quizzes being taken, memberships), even with consent:
// a replay of someone typing their phone number into checkout is not
// analytics anyone needs, and buyers there may be under 18.
import { safeLocalStorage } from './safeStorage';

const KEY = 'ml_consent_v1';
const GA_ID = 'G-8DCGJ38NGV';
const CLARITY_ID = 'wvhry2n5p7';
const TRUSTPILOT_KEY = 'eOgnpVBy7DQKuxn6';

export const CONSENT_EVENT = 'ml:consent-change';
export const OPEN_SETTINGS_EVENT = 'ml:consent-open';

// Buyer-side routes: no session replay here, ever.
export const NO_REPLAY_ROUTES = [
  /^\/checkout\//, /^\/payment\//, /^\/download\//, /^\/forms\/download\//, /^\/ticket\//,
  /^\/quiz\//, /^\/q\//, /^\/qb\//, /^\/bundle\//, /^\/member\//, /^\/join\//, /^\/buyer\//,
  /^\/rate\//, /^\/track\//, /^\/upload\//, /^\/review-order\//, /^\/service-order\//,
  /^\/e\//, /^\/m\//, /^\/o\/[^/]+\/c\/[^/]+\/checkout/, /^\/o\/[^/]+\/library/,
  /^\/c\/[^/]+\/checkout/, /^\/library/, /^\/support\//, /^\/shop\//, /^\/form\//,
  /^\/review\//, /^\/service\//,
];

export const isNoReplayRoute = (pathname = '') => NO_REPLAY_ROUTES.some((re) => re.test(pathname));

/** 'granted' | 'denied' | null (not asked yet). */
export const getConsent = () => {
  try {
    const raw = safeLocalStorage.getItem(KEY);
    if (!raw) return null;
    const v = JSON.parse(raw);
    return v && (v.analytics === 'granted' || v.analytics === 'denied') ? v.analytics : null;
  } catch {
    return null;
  }
};

export const hasAnalyticsConsent = () => getConsent() === 'granted';

export const setConsent = (choice) => {
  const value = choice === 'granted' ? 'granted' : 'denied';
  const before = getConsent();
  safeLocalStorage.setItem(KEY, JSON.stringify({ analytics: value, at: new Date().toISOString() }));
  try {
    window.dispatchEvent(new CustomEvent(CONSENT_EVENT, { detail: value }));
  } catch {
    /* old browsers without CustomEvent: the next page load applies it */
  }
  // Withdrawing can't unload a script that is already running; a reload is
  // the only honest way to stop it. Only when something was actually loaded.
  if (before === 'granted' && value === 'denied' && loaded.size) {
    try { window.location.reload(); } catch { /* ignore */ }
  }
};

export const openConsentSettings = () => {
  try { window.dispatchEvent(new Event(OPEN_SETTINGS_EVENT)); } catch { /* ignore */ }
};

// ── Loaders ──────────────────────────────────────────────────────────────────

const loaded = new Set();

const inject = (src) => {
  const s = document.createElement('script');
  s.async = true;
  s.src = src;
  document.head.appendChild(s);
};

function loadGA() {
  if (loaded.has('ga')) return;
  loaded.add('ga');
  window.dataLayer = window.dataLayer || [];
  window.gtag = window.gtag || function gtag() { window.dataLayer.push(arguments); }; // eslint-disable-line prefer-rest-params
  window.gtag('js', new Date());
  window.gtag('config', GA_ID, { send_page_view: false });
  inject(`https://www.googletagmanager.com/gtag/js?id=${GA_ID}`);
}

function loadClarity() {
  if (loaded.has('clarity')) return;
  loaded.add('clarity');
  window.clarity = window.clarity || function clarity() { (window.clarity.q = window.clarity.q || []).push(arguments); }; // eslint-disable-line prefer-rest-params
  inject(`https://www.clarity.ms/tag/${CLARITY_ID}`);
}

function loadTrustpilot() {
  if (loaded.has('tp')) return;
  loaded.add('tp');
  window.TrustpilotObject = 'tp';
  window.tp = window.tp || function tp() { (window.tp.q = window.tp.q || []).push(arguments); }; // eslint-disable-line prefer-rest-params
  inject('https://invitejs.trustpilot.com/tp.min.js');
  window.tp('register', TRUSTPILOT_KEY);
}

/**
 * Called on every route change (App.jsx). Loads what consent allows, and
 * pauses Clarity on buyer-side pages. Safe to call repeatedly.
 */
export function applyConsent(pathname = window.location.pathname) {
  if (typeof window === 'undefined' || !hasAnalyticsConsent()) return;
  loadGA();
  loadTrustpilot();
  const noReplay = isNoReplayRoute(pathname);
  try {
    // Belt and braces: Clarity masks everything inside this attribute, so
    // even a stop() that arrives late records nothing readable.
    if (noReplay) document.body.setAttribute('data-clarity-mask', 'true');
    else document.body.removeAttribute('data-clarity-mask');
  } catch { /* ignore */ }
  if (noReplay) {
    if (loaded.has('clarity') && typeof window.clarity === 'function') window.clarity('stop');
    return;
  }
  if (loaded.has('clarity')) {
    if (typeof window.clarity === 'function') window.clarity('start');
  } else {
    loadClarity();
  }
}
